/**
 * RouteMetaManager — Sets correct SEO meta tags for EVERY route.
 *
 * Runs on every navigation. Creates/updates meta tags directly in the DOM
 * (not via react-helmet) so they're available within Emergent's 5-second
 * edge-render window.
 *
 * Individual page components (GeoServicePage, BlogArticle, etc.) can
 * override these defaults by calling setPageMeta() from their own useEffect.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE = 'https://localbrandingpro.com';
const BRAND = 'Local Branding Pro';
const DEFAULT_DESC = 'All-in-one local marketing platform. Manage listings, reviews, local SEO, Google Ads, social media, call tracking, and AI visibility — starting at $149/mo.';
const DEFAULT_TITLE = 'Local Branding Pro — Local SEO, Listings, Reviews & Marketing for Small Business';
const DEFAULT_IMAGE = `${SITE}/og-image.png`;

// Route → meta mapping for all static pages
const ROUTE_META = {
  '/': {
    title: DEFAULT_TITLE,
    desc: DEFAULT_DESC,
  },
  '/blog': {
    title: 'Blog | Local Branding Pro',
    desc: 'Marketing tips, SEO strategies, and local business growth insights from Local Branding Pro.',
  },
  '/compare': {
    title: 'Compare Local Marketing Platforms | Local Branding Pro',
    desc: 'See how Local Branding Pro compares to Yext, BrightLocal, Vendasta, and GoHighLevel.',
  },
  '/compare/brightlocal': {
    title: 'Local Branding Pro vs BrightLocal | Comparison',
    desc: 'Compare Local Branding Pro and BrightLocal — features, pricing, and which is right for your local business.',
  },
  '/compare/vendasta': {
    title: 'Local Branding Pro vs Vendasta | Comparison',
    desc: 'Compare Local Branding Pro and Vendasta — features, pricing, and which fits your agency.',
  },
  '/compare/gohighlevel': {
    title: 'Local Branding Pro vs GoHighLevel | Comparison',
    desc: 'Compare Local Branding Pro and GoHighLevel — which all-in-one platform is better for local businesses?',
  },
  '/compare/yext': {
    title: 'Local Branding Pro vs Yext | Comparison',
    desc: 'Compare Local Branding Pro and Yext — listings management, pricing, and features compared.',
  },
  '/warm-handoffs': {
    title: 'Warm Handoffs — Cross-Brand Lead Sharing | Local Branding Pro',
    desc: 'Connect with partner businesses and share qualified leads through our Warm Handoffs network.',
  },
  '/signup': {
    title: 'Sign Up | Local Branding Pro',
    desc: 'Create your Local Branding Pro account. Start managing your local marketing today.',
  },
  '/privacy': {
    title: 'Privacy Policy | Local Branding Pro',
    desc: 'Local Branding Pro privacy policy — how we collect, use, and protect your data.',
  },
  '/terms': {
    title: 'Terms of Service | Local Branding Pro',
    desc: 'Local Branding Pro terms of service — our agreement with you.',
  },
  '/sms-compliance': {
    title: 'SMS Compliance | Local Branding Pro',
    desc: 'Local Branding Pro SMS compliance policy — TCPA, opt-in, and messaging terms.',
  },
  '/locations': {
    title: 'Service Locations | Local Branding Pro',
    desc: 'Local Branding Pro serves businesses across the United States. Browse our service locations.',
  },
  '/admin/login': {
    title: 'Admin Login | Local Branding Pro',
    desc: 'Admin login for Local Branding Pro.',
    noindex: true,
  },
  '/client/login': {
    title: 'Client Login | Local Branding Pro',
    desc: 'Client dashboard login for Local Branding Pro.',
    noindex: true,
  },
};

function ensureMeta(selector, attr, value, createAttrs) {
  let el = document.querySelector(selector);
  if (!el && createAttrs) {
    el = document.createElement('meta');
    Object.entries(createAttrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  if (el) el.setAttribute(attr, value);
}

function ensureLink(rel, attr, value) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

export default function RouteMetaManager() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;

    // Skip admin/client dashboard interior pages
    if (path.startsWith('/admin') && path !== '/admin/login') return;
    if (path.startsWith('/client') && path !== '/client/login') return;

    // Geo pages are handled by useGeoSEO hook — skip here
    if (/^\/locations\/[^/]+\/[^/]+\/[^/]+$/.test(path)) return;

    // Match route or generate defaults
    let meta = ROUTE_META[path];

    if (!meta) {
      // Dynamic route patterns
      if (path.startsWith('/blog/')) {
        // Blog articles handle their own meta — set a generic fallback
        meta = { title: 'Blog | Local Branding Pro', desc: 'Read the latest from Local Branding Pro.' };
      } else if (path.startsWith('/industries/')) {
        const slug = path.split('/')[2] || '';
        const name = slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        meta = {
          title: `${name} Marketing Solutions | Local Branding Pro`,
          desc: `Digital marketing services for ${name.toLowerCase()} businesses. Local SEO, reviews, social media, and more.`,
        };
      } else if (path.startsWith('/locations/')) {
        const parts = path.split('/').filter(Boolean);
        if (parts.length === 2) {
          // /locations/:state
          const state = parts[1].replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          meta = { title: `${state} Marketing Services | Local Branding Pro`, desc: `Local Branding Pro digital marketing services in ${state}.` };
        } else if (parts.length === 3) {
          // /locations/:state/:city
          const city = parts[2].replace(/-[a-z]{2}$/, '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          meta = { title: `${city} Digital Marketing | Local Branding Pro`, desc: `Local Branding Pro services for ${city} businesses.` };
        }
      }
    }

    if (!meta) {
      meta = { title: DEFAULT_TITLE, desc: DEFAULT_DESC };
    }

    const fullUrl = `${SITE}${path}`;
    const title = meta.title || DEFAULT_TITLE;
    const desc = meta.desc || DEFAULT_DESC;

    // Set document title
    document.title = title;

    // Core meta
    ensureMeta('meta[name="description"]', 'content', desc, { name: 'description' });
    ensureMeta('meta[name="robots"]', 'content', meta.noindex ? 'noindex, nofollow' : 'index, follow', { name: 'robots' });

    // Open Graph
    ensureMeta('meta[property="og:type"]', 'content', 'website', { property: 'og:type' });
    ensureMeta('meta[property="og:title"]', 'content', title, { property: 'og:title' });
    ensureMeta('meta[property="og:description"]', 'content', desc, { property: 'og:description' });
    ensureMeta('meta[property="og:url"]', 'content', fullUrl, { property: 'og:url' });
    ensureMeta('meta[property="og:site_name"]', 'content', BRAND, { property: 'og:site_name' });
    ensureMeta('meta[property="og:image"]', 'content', DEFAULT_IMAGE, { property: 'og:image' });

    // Twitter
    ensureMeta('meta[name="twitter:card"]', 'content', 'summary_large_image', { name: 'twitter:card' });
    ensureMeta('meta[name="twitter:title"]', 'content', title, { name: 'twitter:title' });
    ensureMeta('meta[name="twitter:description"]', 'content', desc, { name: 'twitter:description' });

    // Canonical — THIS IS THE CRITICAL FIX
    // Each page gets its own canonical URL, not the homepage
    ensureLink('canonical', 'href', fullUrl);

  }, [location.pathname]);

  return null;
}
